import { Injectable } from '@angular/core';
import { mapOrderType } from '@epicuro-next/data/baskets-grouping';
import { ProductListType } from '@epicuro-next/data/engagement/engagement-models';
import { ListProductReference } from '@epicuro-next/data/lists';
import { filterOutUnavailableDeliveryInfo } from '@epicuro-next/features/engagement/list-utils';
import { BasketItemSource } from '@epicuro-next/models/basket';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import {
  BasketUpdateShops,
  selectDeliveryShops,
} from '@epicuro-next/state/basket-state';
import {
  selectProductSource,
  selectSetDefaultListSourceNeeded,
} from '@epicuro-next/state/config-strip-state';
import {
  CommercialListControlAction,
  selectListEntityFactory,
} from '@epicuro-next/state/list-entities';
import { selectCommercialListToMdoEnabled } from '@epicuro-next/state/product-list-state';
import { go } from '@epicuro-next/state/router-store';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { UBCMessageName } from '@epicuro-next/state/ubc-communication/core';
import {
  updateBasket,
  updateBasketError,
  updateBasketSuccess,
} from '@epicuro-next/state/ubc-communication/ubc-basket';
import { getDeliveryShopId, isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import * as Sentry from '@sentry/angular';
import { BasketUpdateItem } from 'graphql-api/baskets-api-interfaces';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { shopTheCommercialList } from './ubc-commercial-list.actions';

@Injectable()
export class UBCCommercialListEffects {
  private deliveryShopsToUpdate$ = this.store.pipe(
    select(selectDeliveryShops),
    filter(isNonNullish),
    filter((accounts) => Boolean(accounts.length)),
    map((accounts) => accounts.map((account) => getDeliveryShopId(account))),
    withLatestFrom(this.store.select(selectCommercialListToMdoEnabled)),
    map(([shops, addToMdoEnabled]) => (addToMdoEnabled ? shops : [shops[0]])), // only sdp as business requirement
  );

  constructor(private actions: Actions, private store: Store) {}

  public shopTheCommercialList$ = createEffect(() =>
    this.actions.pipe(
      ofType(shopTheCommercialList),
      switchMap((action) =>
        this.store.pipe(
          select(selectListEntityFactory(action.payload.listId)),
          take(1),
          concatLatestFrom(() => [
            this.store.pipe(
              select(selectProductSource),
              map(({ type }) => type as BasketItemSource),
            ),
            this.store.pipe(select(selectSetDefaultListSourceNeeded)),
          ]),
          map(([entity, source, isSetDefaultNeeded]) => ({
            productReferences: action.payload.productReferences,
            source: isSetDefaultNeeded ? BasketItemSource.LIST_LOCAL : source,
            listId: action.payload.listId,
            controlActions: entity?.commercialListControlActions ?? [],
          })),
        ),
      ),
      concatLatestFrom(() => this.deliveryShopsToUpdate$),
      map(
        ([
          { source, listId, productReferences, controlActions },
          deliveryShops,
        ]) => {
          if (!source) {
            Sentry.captureException(
              new Error(`No source information for commercial list ${listId}`),
            );
          }

          const basketItemSource = listItemSourceToBasketItemSource(
            source ?? BasketItemSource.LIST_LOCAL,
            listId,
          );

          let deliveryItems = productReferencesToDeliveryItems(
            filterOutUnavailableDeliveryInfo(productReferences),
            { ...basketItemSource },
          );

          deliveryItems = applyDocumentControl(deliveryItems, controlActions);

          const shops: BasketUpdateShops = {};

          deliveryShops.forEach((shop) => {
            shops[shop] = deliveryItems;
          });

          return updateBasket({
            messageName: UBCMessageName.UpdateBasket,
            payload: {
              shops,
              initiatorSource: UBCMessageName.ShopTheCommercialList,
              updateStrategy: 'add',
            },
          });
        },
      ),
    ),
  );

  public redirectToBasketAfterUpdate$ = createEffect(() =>
    this.actions.pipe(
      ofType(updateBasketSuccess),
      filter(
        (action) =>
          action.payload.source === UBCMessageName.ShopTheCommercialList,
      ),
      map((action) =>
        go({
          commands: [
            '/',
            APP_PRIVATE_PATHS.basket.path,
            action.payload.basketId,
          ],
        }),
      ),
    ),
  );

  public showUpdateBasketError$ = createEffect(() =>
    this.actions.pipe(
      ofType(updateBasketError),
      filter(
        (action) =>
          action.payload.source === UBCMessageName.ShopTheCommercialList,
      ),
      map(() =>
        showSnackBarMessage({
          message: $localize`:CommercialLists|update basket error@@commercialListsUpdateBasketError:There was an error while trying to buy this basket. Please consult to your sales representitive.`,
          messageType: MessageType.WARNING,
        }),
      ),
    ),
  );
}

export function productReferencesToDeliveryItems(
  references: ListProductReference[],
  overrideBasketUpdateItem: Pick<
    BasketUpdateItem,
    'source' | 'originInformation'
  >,
) {
  const itemsPerVariant = references
    .map(({ brandId, deliveryInfo }) =>
      deliveryInfo
        ? deliveryInfo.filter(isNonNullish).map(
            (i): BasketUpdateItem => ({
              brandId,
              productId: i.productSizeId,
              requestedQuantity: i.quantity,
              requestedDeliveryDate: i.deliveryDate,
              displayDateFrom: i.displayDateFrom,
              orderType: mapOrderType(i.orderType),
              ...overrideBasketUpdateItem,
            }),
          )
        : undefined,
    )
    .filter(isNonNullish);

  return ([] as BasketUpdateItem[]).concat(...itemsPerVariant);
}

function applyDocumentControl(
  items: BasketUpdateItem[],
  actions: CommercialListControlAction[],
): BasketUpdateItem[] {
  return items.map((item) => {
    const clone = { ...item };

    actions.forEach((action) => {
      if (action.type === 'multiplier') {
        clone.requestedQuantity =
          clone.requestedQuantity * action.multiplyValue;
      }
    });

    return clone;
  });
}

function listItemSourceToBasketItemSource(
  listSource: BasketItemSource,
  listId: string,
): Pick<BasketUpdateItem, 'source' | 'originInformation'> {
  return {
    source: {
      type: listSource,
    },
    originInformation: {
      listId,
      isCommercial: true,
      listType: ProductListType.Commercial,
    },
  };
}
